import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {GlowText} from "../components/dimus/GlowText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * VERDADES DIFÍCEIS DE ENGOLIR #08 — "E se você sumisse 30 dias?"
 * Editing Style: CINEMATIC
 * Cumpre promessa do #07: "teste do e se eu sumir 30 dias"
 * Evolução: Checklist animado, countdown visual, gargalo highlight
 */

const ORANGE = "#fb923c";
const ORANGE_DARK = "#1a0f05";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const RED = "#F87171";
const GREEN = "#00DF82";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 222, file: "audio/verdade08/verdade08_c01.mp3", dir: "right" as const},
  {dur: 256, file: "audio/verdade08/verdade08_c02.mp3", dir: "left" as const},
  {dur: 607, file: "audio/verdade08/verdade08_c03.mp3", dir: "up" as const},
  {dur: 286, file: "audio/verdade08/verdade08_c04.mp3", dir: "right" as const},
  {dur: 191, file: "audio/verdade08/verdade08_c05.mp3", dir: "up" as const},
];

export const VERDADE08_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`vpx8-${i}`) * 1080;
        const baseY = random(`vpy8-${i}`) * 1920;
        const size = 2 + random(`vps8-${i}`) * 3;
        const speed = 0.2 + random(`vpsp8-${i}`) * 0.35;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.07 + random(`vpo8-${i}`) * 0.1,
          }} />
        );
      })}
    </>
  );
};

// ─── Checklist Item ──────────────────────────────────────────────────
const CheckItem: React.FC<{
  text: string; status: "ok" | "fail" | "question"; delay: number;
}> = ({text, status, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const color = status === "ok" ? GREEN : status === "fail" ? RED : MUTED;
  const icon = status === "ok" ? "✓" : status === "fail" ? "✗" : "?";

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateX(${interpolate(p, [0, 1], [30, 0], CLAMP)}px)`,
      display: "flex", alignItems: "center", gap: 16, width: "85%",
      padding: "14px 20px", borderRadius: 14,
      background: `${color}08`, border: `1px solid ${color}22`,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%", display: "flex",
        alignItems: "center", justifyContent: "center",
        background: `${color}20`, border: `1px solid ${color}44`,
        fontSize: 20, fontWeight: 900, color, fontFamily: FONT_STACK,
      }}>
        {icon}
      </div>
      <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 600, color: WHITE, fontFamily: FONT_STACK}}>
        {text}
      </div>
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const countP = spring({frame: frame - 40, fps, config: SPRINGS.heavy});

  return (
    <OrganicLayer intensity="subtle" seed="verdade-08">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
      <GradientText text="VERDADE #08" colors={[ORANGE, RED]} fontSize={FONT_SCALE.h1.max} springProfile="bouncy" />
      <div style={{
        opacity: countP,
        transform: `scale(${interpolate(countP, [0, 1], [0.8, 1], CLAMP)})`,
        fontSize: 140, fontWeight: 900, color: ORANGE,
        fontFamily: FONT_STACK, textShadow: `0 0 40px ${ORANGE}40`,
      }}>
        30
      </div>
      <div style={{
        opacity: countP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK,
      }}>
        dias sem você.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 2: AS PERGUNTAS — checklist ────────────────────────────────
const S2: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 12}}>
    <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
    <CheckItem text="Pedidos continuam?" status="question" delay={10} />
    <CheckItem text="Clientes atendidos?" status="question" delay={40} />
    <CheckItem text="Financeiro fecha?" status="question" delay={70} />
    <CheckItem text="Entregas no prazo?" status="question" delay={100} />
    <CheckItem text="Ou tudo para?" status="fail" delay={150} />
  </AbsoluteFill>
);

// ─── SCENE 3: A VERDADE ──────────────────────────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Fase A (0-250): "Não tem empresa, tem emprego"
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 80, fps, config: SPRINGS.smooth});

  // Fase B (250+): "Insubstituível não é elogio, é risco"
  const showB = frame >= 300;
  const pB = spring({frame: frame - 310, fps, config: SPRINGS.heavy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

      {frame < 320 && (
        <>
          <div style={{
            opacity: p1, transform: `scale(${interpolate(p1, [0, 1], [0.9, 1], CLAMP)})`,
            fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: RED,
            fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
          }}>
            Você não tem uma empresa.
          </div>
          <div style={{
            opacity: p2, fontSize: FONT_SCALE.h2.min, fontWeight: 700,
            color: WHITE, fontFamily: FONT_STACK, textAlign: "center",
          }}>
            Tem um emprego que paga bem.
          </div>
        </>
      )}

      {showB && (
        <div style={{opacity: pB, textAlign: "center"}}>
          <GlowText text="Insubstituível" delay={320} size={FONT_SCALE.h1.min} color={ORANGE} />
          <div style={{
            opacity: spring({frame: frame - 400, fps, config: SPRINGS.smooth}),
            fontSize: FONT_SCALE.h2.min, fontWeight: 600, color: RED,
            fontFamily: FONT_STACK, marginTop: 12,
          }}>
            não é elogio. É risco.
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};

// ─── SCENE 4: O CAMINHO ──────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 80, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left + 20}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <div style={{
        opacity: p1, transform: `translateX(${interpolate(p1, [0, 1], [-40, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
        fontFamily: FONT_STACK, lineHeight: 1.2,
      }}>
        Tire de você a decisão.{"\n"}Não a operação.
      </div>
      <div style={{
        opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "90%",
      }}>
        Se você é o único que decide preço,{"\n"}aprova projeto, responde cliente grande{"\n"}— você é o gargalo.
      </div>
      <GlowText text="Gargalo não escala." delay={160} size={FONT_SCALE.h2.min} color={ORANGE} />
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA ────────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const textP = spring({frame: frame - 8, fps, config: SPRINGS.heavy});
  const profileP = spring({frame: frame - 60, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 100, fps, config: SPRINGS.bouncy});
  const pulse = frame > 120 ? 1 + Math.sin((frame - 120) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
      <div style={{
        opacity: textP, transform: `translateY(${interpolate(textP, [0, 1], [15, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 600, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3, maxWidth: "88%",
      }}>
        Anota o que para sem você.{"\n"}Essa é a lista.
      </div>
      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${ORANGE}`, boxShadow: `0 0 20px ${ORANGE}30`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Verdades difíceis</div>
        </div>
      </div>
      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: ORANGE,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px ${ORANGE}40`,
      }}>
        SEGUE PRA MAIS
      </div>
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

export const Verdade08: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, ${ORANGE_DARK}, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={25} color={ORANGE} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
